import type { CSSProperties } from "react";

/** 空间首页 Hero：头像（圆形，尺寸为正方形边长） */
export interface SpaceHomeAvatarConfig {
  /** 直径（px），宽高相等 */
  size: number;
  /** 距 Hero 顶部的距离（px） */
  marginTop: number;
  /** 头像下方间距（px） */
  marginBottom: number;
  border: {
    width: number;
    color: string;
  };
}

/** 空间首页 Hero：第一行（空间名称 + 认证） */
export interface SpaceHomeNameRowConfig {
  /** 名称字号（px） */
  nameFontSize: number;
  /** 认证标签最小高度（px） */
  certMinHeight: number;
  /** 名称与认证标签间距（px） */
  gap: number;
}

/** 空间首页 Hero：空间介绍（名称行下方） */
export interface SpaceHomeIntroRowConfig {
  /** 字号（px） */
  fontSize: number;
  /** 文字颜色 */
  color: string;
  /** 最大宽度（px） */
  maxWidth: number;
}

/** 空间首页 Hero：指标区（粉丝 / 订阅 / 空间 ID） */
export interface SpaceHomeMetricsRowConfig {
  /** 数值字号（px） */
  valueFontSize: number;
  /** 标签字号（px） */
  labelFontSize: number;
  /** 数值与标签间距（px） */
  valueLabelGap: number;
  /** 列间距（px），控制粉丝 / 订阅 / ID 三列之间的间距 */
  columnsGap: number;
  /** 每列左右内边距（px） */
  columnPaddingX: number;
  /** 指标区最大宽度（px）；0 表示不限制 */
  maxWidth: number;
  /** 数值金融字体族 */
  valueFontFamily: string;
  /** 分隔线高度（px） */
  dividerHeight: number;
  /** 分隔线宽度（px） */
  dividerWidth: number;
  /** 分隔线颜色 */
  dividerColor: string;
}

/** 空间首页 Hero：操作按钮（关注 / 订阅） */
export interface SpaceHomeActionButtonConfig {
  width: number;
  height: number;
  borderRadius: number;
  fontSize: number;
}

/** 空间首页 Hero 布局配置 */
export interface SpaceHomeHeroLayoutConfig {
  avatar: SpaceHomeAvatarConfig;
  nameRow: SpaceHomeNameRowConfig;
  introRow: SpaceHomeIntroRowConfig;
  metricsRow: SpaceHomeMetricsRowConfig;
  followButton: SpaceHomeActionButtonConfig;
  subscribeButton: SpaceHomeActionButtonConfig;
  /** 两个操作按钮间距（px） */
  actionButtonsGap: number;
  /** 操作按钮行距上方间距（px），相对指标区 */
  actionButtonsMarginTop: number;
  /** Hero 区块距下方内容的间距（px） */
  marginBottom: number;
  /** 各行之间的间距（px） */
  rowGap: number;
}

/** 空间首页 Hero 布局配置（各元素 JSON 分组） */
export const SPACE_HOME_HERO_LAYOUT = {
  avatar: {
    size: 250,
    marginTop: 20,
    marginBottom: 16,
    border: {
      width: 1,
      color: "#f9a8d4",
    },
  },
  nameRow: {
    nameFontSize: 18,
    certMinHeight: 24,
    gap: 8,
  },
  introRow: {
    fontSize: 14,
    color: "rgba(0, 0, 0, 0.55)",
    maxWidth: 380,
  },
  metricsRow: {
    valueFontSize: 16,
    labelFontSize: 12,
    valueLabelGap: 6,
    columnsGap: 32,
    columnPaddingX: 8,
    maxWidth: 380,
    valueFontFamily:
      '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, monospace',
    dividerHeight: 36,
    dividerWidth: 0.5,
    dividerColor: "rgba(0, 0, 0, 0.12)",
  },
  followButton: {
    width: 120,
    height: 38,
    borderRadius: 6,
    fontSize: 14,
  },
  subscribeButton: {
    width: 120,
    height: 38,
    borderRadius: 6,
    fontSize: 14,
  },
  actionButtonsGap: 16,
  actionButtonsMarginTop: 20,
  marginBottom: 28,
  rowGap: 12,
} as const satisfies SpaceHomeHeroLayoutConfig;

/** 空间首页 Hero 布局 CSS 变量 */
export function spaceHomeHeroLayoutCssVars(): CSSProperties {
  const {
    avatar,
    nameRow,
    introRow,
    metricsRow,
    followButton,
    subscribeButton,
    actionButtonsGap,
    actionButtonsMarginTop,
    marginBottom,
    rowGap,
  } = SPACE_HOME_HERO_LAYOUT;
  return {
    ["--space-home-hero-margin-bottom" as string]: `${marginBottom}px`,
    ["--space-home-row-gap" as string]: `${rowGap}px`,
    ["--space-home-avatar-size" as string]: `${avatar.size}px`,
    ["--space-home-avatar-margin-top" as string]: `${avatar.marginTop}px`,
    ["--space-home-avatar-margin-bottom" as string]: `${avatar.marginBottom}px`,
    ["--space-home-avatar-border-width" as string]: `${avatar.border.width}px`,
    ["--space-home-avatar-border-color" as string]: avatar.border.color,
    ["--space-home-name-font-size" as string]: `${nameRow.nameFontSize}px`,
    ["--space-home-name-row-gap" as string]: `${nameRow.gap}px`,
    ["--space-home-cert-min-height" as string]: `${nameRow.certMinHeight}px`,
    ["--space-home-intro-font-size" as string]: `${introRow.fontSize}px`,
    ["--space-home-intro-color" as string]: introRow.color,
    ["--space-home-intro-max-width" as string]: `${introRow.maxWidth}px`,
    ["--space-home-metrics-value-font-size" as string]: `${metricsRow.valueFontSize}px`,
    ["--space-home-metrics-label-font-size" as string]: `${metricsRow.labelFontSize}px`,
    ["--space-home-metrics-value-label-gap" as string]: `${metricsRow.valueLabelGap}px`,
    ["--space-home-metrics-columns-gap" as string]: `${metricsRow.columnsGap}px`,
    ["--space-home-metrics-column-padding-x" as string]: `${metricsRow.columnPaddingX}px`,
    ["--space-home-metrics-max-width" as string]:
      metricsRow.maxWidth > 0 ? `${metricsRow.maxWidth}px` : "none",
    ["--space-home-metrics-value-font-family" as string]: metricsRow.valueFontFamily,
    ["--space-home-metrics-divider-height" as string]: `${metricsRow.dividerHeight}px`,
    ["--space-home-metrics-divider-width" as string]: `${metricsRow.dividerWidth}px`,
    ["--space-home-metrics-divider-color" as string]: metricsRow.dividerColor,
    ["--space-home-follow-btn-width" as string]: `${followButton.width}px`,
    ["--space-home-follow-btn-height" as string]: `${followButton.height}px`,
    ["--space-home-follow-btn-radius" as string]: `${followButton.borderRadius}px`,
    ["--space-home-follow-btn-font-size" as string]: `${followButton.fontSize}px`,
    ["--space-home-subscribe-btn-width" as string]: `${subscribeButton.width}px`,
    ["--space-home-subscribe-btn-height" as string]: `${subscribeButton.height}px`,
    ["--space-home-subscribe-btn-radius" as string]: `${subscribeButton.borderRadius}px`,
    ["--space-home-subscribe-btn-font-size" as string]: `${subscribeButton.fontSize}px`,
    ["--space-home-action-buttons-gap" as string]: `${actionButtonsGap}px`,
    ["--space-home-action-buttons-margin-top" as string]: `${actionButtonsMarginTop}px`,
    ["--space-home-membership-card-max-width" as string]: `${SPACE_HOME_MEMBERSHIP_CARD_MAX_WIDTH}px`,
  };
}

/** 空间首页订阅（会员）卡片区域最大宽度（px），与 .space-home max-width 一致 */
export const SPACE_HOME_MEMBERSHIP_CARD_MAX_WIDTH = 600;

/** 页面正文区宽度下限（px），与 .space-content min-width 一致 */
export const SPACE_PAGE_CONTENT_MIN_WIDTH = 1200;

/** 页面正文区宽度上限（px），与 .space-content max-width 一致 */
export const SPACE_PAGE_CONTENT_MAX_WIDTH = 1400;

/** 页面正文区宽度占比（%），与 .space-content width 一致 */
export const SPACE_PAGE_CONTENT_WIDTH_PERCENT = 90;

/** 未订阅引导海报：相对视区垂直中心的向上偏移（px），正值上移、负值下移 */
export const SPACE_SUBSCRIBE_GATE_POSTER_CENTER_OFFSET_Y = 20;

/** 未订阅引导海报：订阅按钮相对海报底边的向上偏移（px） */
export const SPACE_SUBSCRIBE_GATE_POSTER_CTA_BOTTOM_OFFSET = 50;

/** 未订阅引导海报：宽度占页面正文区（1200–1400 那列）宽度的百分比 */
export const SPACE_SUBSCRIBE_GATE_POSTER_WIDTH_PERCENT = 80;

/** 未订阅引导海报：最小宽度（px） */
export const SPACE_SUBSCRIBE_GATE_POSTER_MIN_WIDTH = 320;

/** 未订阅引导海报：最大宽度（px） */
export const SPACE_SUBSCRIBE_GATE_POSTER_MAX_WIDTH = 1000;

/** 未订阅引导海报：订阅按钮背景色 */
export const SPACE_SUBSCRIBE_GATE_SUBSCRIBE_BTN_BG = "#fa8c16";

/** 未订阅引导海报：「非卖品」文案背景色（叠在海报上时） */
export const SPACE_SUBSCRIBE_GATE_NO_SPEC_HINT_BG = "rgba(252, 68, 249, 0.68)";

/** 未订阅引导页布局 CSS 变量 */
export function spaceSubscribeGateLayoutCssVars(): CSSProperties {
  return {
    ["--space-subscribe-gate-poster-offset-y" as string]: `${SPACE_SUBSCRIBE_GATE_POSTER_CENTER_OFFSET_Y}px`,
    ["--space-subscribe-gate-poster-cta-bottom-offset" as string]: `${SPACE_SUBSCRIBE_GATE_POSTER_CTA_BOTTOM_OFFSET}px`,
    ["--space-subscribe-gate-poster-width-percent" as string]: `${SPACE_SUBSCRIBE_GATE_POSTER_WIDTH_PERCENT}`,
    ["--space-subscribe-gate-poster-min-width" as string]: `${SPACE_SUBSCRIBE_GATE_POSTER_MIN_WIDTH}px`,
    ["--space-subscribe-gate-poster-max-width" as string]: `${SPACE_SUBSCRIBE_GATE_POSTER_MAX_WIDTH}px`,
    ["--space-subscribe-gate-subscribe-btn-bg" as string]: SPACE_SUBSCRIBE_GATE_SUBSCRIBE_BTN_BG,
    ["--space-subscribe-gate-no-spec-hint-bg" as string]: SPACE_SUBSCRIBE_GATE_NO_SPEC_HINT_BG,
    ["--space-page-content-min-width" as string]: `${SPACE_PAGE_CONTENT_MIN_WIDTH}px`,
    ["--space-page-content-max-width" as string]: `${SPACE_PAGE_CONTENT_MAX_WIDTH}px`,
    ["--space-page-content-width-percent" as string]: `${SPACE_PAGE_CONTENT_WIDTH_PERCENT}`,
  };
}
